import { createApp } from 'vue';
import App from './App.vue';
import router from './router';

import './assets/tailwind.css';
import { useModalStack } from './components/core/Modal/useModalStack';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faBars,
  faChevronDown,
  faChevronUp,
  faCircleChevronDown,
  faCircleChevronUp,
  faCircleNotch,
  faCircleInfo,
  faCircleQuestion,
  faBox,
  faShip,
  faClock,
  faTrain,
  faTruck,
  faBoxArchive,
  faCalculator,
  faCheck,
  faCopy,
  faClipboard,
  faEye,
  faGear,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowDown,
  faLocationDot,
  faBuilding,
  faPlus,
  faMinus,
  faPrint,
  faRoute,
  faStickyNote,
  faPen,
  faWrench,
  faPenToSquare,
  faPlay,
  faCircleCheck,
  faCircleXmark,
  faRotateLeft,
  faRotateRight,
  faRightFromBracket,
  faTag,
  faTriangleExclamation,
  faTrash,
  faUser,
  faEllipsisVertical,
  faBan,
  faEnvelope,
  faFileLines,
  faXmark,
  faCalendarDay,
  faWarehouse,
  faFileInvoice,
  faClockRotateLeft,
  faDownload,
  faChartSimple,
  faTruckMoving,
  faFileSignature,
  faGripVertical,
  faCloudArrowUp,
  faFilePdf,
  faBold,
  faTextHeight,
  faBorderAll,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faFilterCircleXmark,
  faSortAmountUp,
  faSortAmountDown,
  faTimes,
  faMagnifyingGlass,
  faArrowUpFromBracket,
  faArrowRotateForward,
  faArrowRotateBack,
  faStar,
  faDatabase,
  faEllipsis,
  faWarning,
  faBoltLightning,
  faTemperatureHigh,
  faSnowflake,
} from '@fortawesome/free-solid-svg-icons';

import * as Sentry from "@sentry/vue";

import { createApolloProvider } from '@vue/apollo-option';
import { DefaultApolloClient } from '@vue/apollo-composable';
import { pinia } from './stores/pinia';
import { useAuthStore } from './stores/auth';
import { apolloClient, performLogout } from './apollo';

import VueTippy from 'vue-tippy';
import 'tippy.js/dist/tippy.css';
import GMap from '@fawmi/vue-google-maps';
import { VueFire } from 'vuefire';
import { appFire } from './components/Comms/db';
import InputField from './components/core/Input/InputField.vue';
import AddNotesArea from './components/core/AddNotesArea.vue';

const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
});

library.add(
  faBars,
  faChevronDown,
  faChevronUp,
  faCircleChevronDown,
  faCircleChevronUp,
  faCircleNotch,
  faCircleInfo,
  faCircleQuestion,
  faBox,
  faShip,
  faClock,
  faTrain,
  faTruck,
  faBoxArchive,
  faCalculator,
  faCheck,
  faCopy,
  faClipboard,
  faEye,
  faGear,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowDown,
  faLocationDot,
  faBuilding,
  faPlus,
  faMinus,
  faPrint,
  faRoute,
  faStickyNote,
  faPen,
  faWrench,
  faPenToSquare,
  faPlay,
  faCircleCheck,
  faCircleXmark,
  faRotateLeft,
  faRotateRight,
  faRightFromBracket,
  faTag,
  faTriangleExclamation,
  faTrash,
  faUser,
  faEllipsisVertical,
  faBan,
  faEnvelope,
  faFileLines,
  faXmark,
  faCalendarDay,
  faWarehouse,
  faFileInvoice,
  faClockRotateLeft,
  faDownload,
  faChartSimple,
  faTruckMoving,
  faFileSignature,
  faGripVertical,
  faCloudArrowUp,
  faFilePdf,
  faBold,
  faTextHeight,
  faBorderAll,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faFilterCircleXmark,
  faSortAmountUp,
  faSortAmountDown,
  faTimes,
  faMagnifyingGlass,
  faArrowUpFromBracket,
  faArrowRotateForward,
  faArrowRotateBack,
  faStar,
  faDatabase,
  faEllipsis,
  faWarning,
  faBoltLightning,
  faTemperatureHigh,
  faSnowflake,
);

const app = createApp(App);
app.use(pinia);

app.directive('click-outside', {
  beforeMount(el, binding) {
    const handler = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return;
      if (el === event.target || el.contains(event.target)) return;
      if (typeof binding.value === 'function') {
        binding.value(event);
      }
    };

    (el as any).__vueClickOutside__ = handler;
    document.addEventListener('click', handler);
    document.addEventListener('touchstart', handler);
  },
  unmounted(el) {
    const handler = (el as any).__vueClickOutside__ as ((event: Event) => void) | undefined;
    if (handler) {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchstart', handler);
      delete (el as any).__vueClickOutside__;
    }
  },
});

app.provide(DefaultApolloClient, apolloClient);

// Provide modal stack globally
const modalStack = useModalStack();
app.provide('modalStack', modalStack);

app.component('font-awesome-icon', FontAwesomeIcon);
app.component('InputField', InputField);
app.component('AddNotesArea', AddNotesArea);


const sentryEnv = process.env.VUE_APP_SENTRY_ENVIRONMENT || 'local';
const sentryDsn = process.env.VUE_APP_SENTRY_DSN;

// Only initialize if we have a DSN AND the environment is NOT local
if (sentryDsn && sentryEnv !== 'local') {
  Sentry.init({
    app,
    dsn: sentryDsn,
    
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
    ],

    initialScope: {
      tags: {
        service: "frontend-vue",
      },
    },

    environment: sentryEnv,
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["localhost", "my-contrak-api.com", /^\//],
    logErrors: true,
  });
  console.log(`Sentry initialized in ${sentryEnv} mode`);
} else {
  console.log(`Sentry DISABLED (Environment: ${sentryEnv})`);
}


app.use(router);

app.use(VueFire, { firebaseApp: appFire });

app.use(GMap, {
  load: {
    key: process.env.VUE_APP_GOOGLE_API_KEY,
    libraries: 'places',
  },
});
app.use(apolloProvider);
app.use(
  VueTippy, 
  {
  directive: 'tippy',
  component: 'tippy',
  defaultProps: {
    placement: 'auto-end',
    allowHTML: true,
  },
}
);

app.config.globalProperties.$capitalize = (value: any) => {
  if (!value) return '';
  value = String(value);
  return value.charAt(0).toUpperCase() + value.slice(1);
};

app.config.globalProperties.$handleEmptyNumber = (num: any): number | any => {
    if (num === '' || num === null || typeof num === 'undefined') {
        return 0;
    }
    const parsedNum = parseFloat(num);
    return isNaN(parsedNum) ? num : parsedNum;
};

app.config.globalProperties.$dollarize = (value: any) => {
  if (!value) return '$';
  return `$${String(value)}`;
};

const checkInitialSession = () => {
  const authStore = useAuthStore(pinia);
  const TWELVE_HOURS = 12 * 60 * 60 * 1000;

  if (authStore.isSessionExpired(TWELVE_HOURS)) {
    performLogout();
    console.log("Session expired due to 12h inactivity.");
  }

  if (authStore.isAuthenticated) {
    authStore.updateLastActivity();
  }
};

checkInitialSession();

app.mount('#app');
